import React from 'react';
import { TableCell, TableRow } from 'src/components/ui/table';
import { ChevronDown, ChevronRight, EyeOff, NotebookPen } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'src/components/ui/tooltip';
import { Invoice } from '../../../../redux/billing/billing.types';
import { formatNumber, formatCurrency, formatLastSynced, getCompanyName, getInvoiceChannelDisplay, getInvoiceTotals } from './utils';
import StatusBadgeThin from './status-badge-thin';
import { Company } from 'src/pages/billing/types/billing.types';

interface InvoiceRowProps {
  invoice: Invoice;
  invoiceIndex: number;
  isExpanded: boolean;
  isFirstRow: boolean;
  isLastRow: boolean;
  isUnassigned: boolean;
  viewHidden: boolean;
  companyLookup: Record<string, Company>;
  selectedGroupId: string;
  toggleInvoiceExpansion: (invoiceId: string) => void;
  updateInvoiceField: (invoiceId: string, field: string, value: any) => void;
  lineItemsNeedAttention: (invoice: Invoice) => boolean;
}

export const InvoiceRow: React.FC<InvoiceRowProps> = ({
  invoice,
  invoiceIndex,
  isExpanded,
  isFirstRow,
  isLastRow,
  isUnassigned,
  viewHidden,
  companyLookup,
  selectedGroupId,
  toggleInvoiceExpansion,
  updateInvoiceField,
  lineItemsNeedAttention,
}) => {
  const totals = getInvoiceTotals(invoice);
  const hasAttentionNeeded = invoice.attention_needed || lineItemsNeedAttention(invoice);
  const hasNotes = invoice.invoice_line_items.some(lineItem => lineItem.notes);

  return (
    <TableRow
      key={invoice.id}
      className={`transition-colors border-b border-gray-100 cursor-pointer hover:bg-gray-201 px-0 ${isExpanded ? 'bg-gray-200 border-l-4 border-r-4 border-yellow-500 border-b-0 border-t-4' : ''}`}
      onClick={() => toggleInvoiceExpansion(invoice.id)}
    >
      {/* <TableCell className='w-[3%] min-w-[30px] border-r border-gray-201' /> */}
      <TableCell
        className={`py-1.5 w-[3%] min-w-[30px] cursor-pointer border-r border-gray-201`}
        onClick={() => toggleInvoiceExpansion(invoice.id)}
      >
        <div>
          {isExpanded ? (
            <ChevronDown className={`h-4 w-4 ${hasAttentionNeeded ? 'text-[#FF0000]' : 'text-gray-500'}`} />
          ) : (
            <ChevronRight className={`h-4 w-4 ${hasAttentionNeeded ? 'text-[#FF0000]' : 'text-gray-500'}`} />
          )}
        </div>
      </TableCell>

      <TableCell
        className="py-1.5 w-[35%] min-w-[250px] max-w-[250px] border-r border-gray-201"
      >
        <div className="flex items-center gap-2 overflow-hidden">
          <div className="min-w-0 flex-1">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className={`flex font-medium truncate text-normal font-bold ${hasAttentionNeeded ? 'text-[#FF0000]' : ''}`}>
                    {invoice.hide && viewHidden && (
                      <span className="flex items-center mr-2">
                        <EyeOff className="h-3 w-3 text-black" />
                      </span>
                    )}
                    {hasNotes && (
                      <span className="flex items-center mr-2">
                        <NotebookPen className="h-3 w-3 text-orange-500" />
                      </span>
                    )}
                    {getCompanyName(invoice.company, companyLookup)}
                  </div>
                </TooltipTrigger>
                <TooltipContent className="max-w-none py-2">
                  <p>
                    {getCompanyName(invoice.company, companyLookup)}
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <div className={`text-xs truncate ${hasAttentionNeeded ? 'text-[#CC0000]' : 'text-gray-500'}`}>
              {invoice.invoice_line_items.filter(lineItem => !lineItem.hide).length}
              {' '}
              line item
              {invoice.invoice_line_items.filter(lineItem => !lineItem.hide).length !== 1 ? 's' : ''}
              {' - '}
              {formatLastSynced(invoice.last_sync)}
            </div>
          </div>
        </div>
      </TableCell>

      <TableCell className={`py-1.5 w-[15%] min-w-[70px] max-w-[70px] text-normal font-bold border-r border-gray-201 ${hasAttentionNeeded ? 'text-[#FF0000]' : ''}`}>
        {getInvoiceChannelDisplay(invoice)}
      </TableCell>

      <TableCell className={`py-1.5 text-right w-[15%] min-w-[100px] max-w-[100px] text-normal font-bold border-r border-gray-201 ${hasAttentionNeeded ? 'text-[#FF0000]' : ''}`}>
        {formatNumber(totals.impressions)}
      </TableCell>

      <TableCell className={`py-1.5 text-right w-[15%] min-w-[100px] max-w-[100px] text-normal font-bold border-r border-gray-201 ${hasAttentionNeeded ? 'text-[#FF0000]' : ''}`}>
        {formatCurrency(totals.amountToBill)}
      </TableCell>

      <TableCell className="py-1.5 text-center w-[15%] min-w-[100px] max-w-[100px] text-normal font-bold">
        <StatusBadgeThin
          status={invoice.invoice_status}
          attention_needed={hasAttentionNeeded}
        />
      </TableCell>
    </TableRow>
  );
};

export default InvoiceRow;
